/* eslint-disable max-lines */
import { Alert, EmailSelectField, FaIcon } from '@acp/core-ui';
import { EmailType, getPropertyContactTypeName } from '@acp/shared';
import {
    Button,
    Checkbox,
    Skeleton,
    Spacer,
    Text,
    useDisclosure,
    VStack,
    Link,
    Box,
    FormControl,
    FormLabel,
    FormErrorMessage,
    HStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFormState } from 'react-hook-form';
import { useAgencySettings, useExperiment, useSession } from '@acp/feature/auth';
import { UseMutationResult } from '@tanstack/react-query';
import React, {
    forwardRef, Suspense, useImperativeHandle, useState,
} from 'react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons';
import { faArrowDownToLine, faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { emailSelectFormSchema, ForwardEmailFormValues } from '../../features/email';
import { SendEmailPreviewModal } from '../modals/send-email-preview-modal';
import { useDownloadMutation } from '../../features/email/hooks/use-download-mutation';
import { usePropertyContactList } from '../../features/property';

const TinyMCEEditorWithImageUpload = React.lazy(() => import('../tinymce-editor/image-upload'));
const TINYMCE_CONTAINER_HEIGHT = 300;

// Define the ref type
export type SendEmailModalRef = {
    resetForm: () => void;
};

type Props = {
    propertyId: number;
    onSubmit: (formValues: ForwardEmailFormValues) => void;
    isEnabled?: boolean;
    onClose?: () => void;
    extraErrorMessage?: string;
    includeLandlords?: boolean;
    includePropertyManagers?: boolean;
    includeOccupants?: boolean;
    allowSubscribe?: boolean;
    previewAvailable?: boolean;
    downloadAvailable?: boolean;
    confirmButtonText?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutation: UseMutationResult<void, Error, any, unknown>;
    metaData: {
        emailType: EmailType;
        noticeId?: number;
        quoteId?: number;
        invoiceId?: number;
        propertyId?: number;
    };
};

export const SendEmailForm = forwardRef<SendEmailModalRef, Props>(
    (
        {
            propertyId,
            onSubmit,
            onClose,
            isEnabled = true,
            extraErrorMessage = '',
            includeLandlords,
            includePropertyManagers,
            includeOccupants,
            allowSubscribe = false,
            previewAvailable = false,
            downloadAvailable = true,
            mutation,
            metaData,
            confirmButtonText,
        }: Props,
        ref
    ) => {
        const previewDisclosure = useDisclosure();
        const { complianceUserEmail } = useSession();
        const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);

        const { data: contacts } = usePropertyContactList(
            propertyId,
            {
                includeLandlords,
                includePropertyManagers,
                includeOccupants,
            },
            isEnabled
        );

        let fromEmailRequired = metaData.emailType === EmailType.OCCUPANT_NOTICE;
        let emailSet = !fromEmailRequired;

        const {
            control, register, handleSubmit, reset, getValues, trigger, setValue,
        } = useForm<ForwardEmailFormValues>({
            resolver: zodResolver(
                emailSelectFormSchema({
                    fromEmailRequired,
                })
            ),
            defaultValues: {
                fromEmail: undefined,
                toEmails: [],
                ccEmails: [],
                defaultRecipient: complianceUserEmail,
                sendToDefaultRecipient: false,
                emailMessage: '',
            },
            mode: 'onChange',
        });

        const { isValid, errors: formErrors } = useFormState({ control });

        const downloadEmailMutation = useDownloadMutation(metaData);

        const { agencySettings, isLoading: agencySettingLoading } = useAgencySettings();
        const { userExperiments, isLoading: experimentLoading } = useExperiment();

        useImperativeHandle(ref, () => ({
            resetForm: () => {
                reset();
                previewDisclosure.onClose();
            },
        }));

        const onDownloadClick = () => {
            const formVals = getValues();
            downloadEmailMutation.mutate({
                message: formVals.emailMessage,
            });
        };

        const getEmailOptions = () => contacts?.map((contact) => ({
            group: getPropertyContactTypeName(contact.type),
            label: `${contact.name} ${
                contact.position !== null && contact.position !== undefined
                    ? `(${contact.position})`
                    : ''
            }`,
            value: contact.email,
        })) ?? [];

        if (agencySettingLoading || experimentLoading) {
            return (
                <VStack align="left" spacing={4}>
                    <Box>
                        <FormLabel>To</FormLabel>
                        <Skeleton p={6} h={2} w="full" />
                    </Box>
                    <Box>
                        <FormLabel>CC</FormLabel>
                        <Skeleton p={6} h={2} w="full" />
                    </Box>
                    <Box>
                        <FormLabel>Add a short message to the email</FormLabel>
                        <Skeleton p={6} h={TINYMCE_CONTAINER_HEIGHT} w="full" />
                    </Box>
                </VStack>
            );
        }

        const emailSetupLinks = agencySettings?.emailDomains?.map((domain) => (
            <Box key={domain.domain}>
                <Link href={domain.setup_url} target="_blank">
                    Setup for&nbsp;
                    <strong>{domain.domain}</strong>
                    &nbsp;now&nbsp;
                    <ExternalLinkIcon mx="2px" />
                </Link>
            </Box>
        ));

        let sendFromComponent = fromEmailRequired && (
            <Alert title="Sending emails from the Compliance Portal is disabled" status="error">
                Please contact your account manager to enable this feature.
            </Alert>
        );

        // if login email is available inside the email domain
        if (
            fromEmailRequired
            && agencySettings?.emailDomains != null
            && agencySettings.emailDomains.length > 0
        ) {
            if (
                agencySettings?.emailDomains.find(
                    (domain) => domain?.email_accounts.find(
                        // eslint-disable-next-line max-len
                        (emailAccount) => emailAccount.email.toLocaleLowerCase()
                                === complianceUserEmail.toLocaleLowerCase()
                    ) != null
                ) == null
            ) {
                sendFromComponent = (
                    <Alert status="warning" title="Seems like your account is not setup yet">
                        <Box>
                            {emailSetupLinks}
                        </Box>
                    </Alert>
                );
            } else {
                sendFromComponent = (
                    <FormControl>
                        <FormLabel>From</FormLabel>
                        <Text
                            as="strong"
                            pl={4}
                            {...register('fromEmail', { value: complianceUserEmail })}
                        >
                            {complianceUserEmail}
                        </Text>
                    </FormControl>
                );
                emailSet = true;
            }
        }

        return (
            <VStack align="left">
                <VStack alignItems="start" gap={4}>
                    {sendFromComponent}
                    <EmailSelectField
                        control={control}
                        name="toEmails"
                        label="To"
                        isLoading={contacts === undefined}
                        options={getEmailOptions()}
                    />
                    <EmailSelectField
                        control={control}
                        name="ccEmails"
                        label="CC"
                        isLoading={contacts === undefined}
                        options={getEmailOptions()}
                    />
                    <FormControl isInvalid={!isValid}>
                        <FormLabel fontWeight="medium">Add a short message to the email</FormLabel>
                        <Suspense fallback={<Skeleton height={TINYMCE_CONTAINER_HEIGHT} />}>
                            <TinyMCEEditorWithImageUpload
                                containerHeight={TINYMCE_CONTAINER_HEIGHT}
                                onStartUpload={() => {
                                    setIsUploadingImage(true);
                                }}
                                onFinishUpload={() => {
                                    setIsUploadingImage(false);
                                }}
                                onEditorChange={(content) => {
                                    setValue('emailMessage', content);
                                    void trigger('emailMessage');
                                }}
                            />
                        </Suspense>
                        <FormErrorMessage>
                            {formErrors?.emailMessage && formErrors?.emailMessage.message}
                        </FormErrorMessage>
                    </FormControl>

                    {allowSubscribe && (
                        <Checkbox
                            {...register('sendToDefaultRecipient')}
                            alignItems="start"
                            flexGrow={1}
                        >
                            <Text lineHeight={1} fontWeight="500">
                                Send a copy to myself and subscribe to notifications
                            </Text>
                            <Text lineHeight={2} fontSize="sm" color="muted.700">
                                {complianceUserEmail}
                            </Text>
                        </Checkbox>
                    )}
                    {mutation?.error != null && (
                        <Text color="red.500" fontSize="sm">
                            {`${extraErrorMessage} (${mutation.error.message})`}
                        </Text>
                    )}
                </VStack>
                <HStack justifyContent="end" pt={6}>
                    {onClose && (
                        <>
                            <Button
                                onClick={onClose}
                                size="md"
                                colorScheme="muted"
                                variant="ghost"
                            >
                                Close
                            </Button>
                            <Spacer />
                        </>
                    )}
                    {downloadAvailable
                        && userExperiments?.AgencyCompliancePortalDownloadEmail === true
                        && (
                            <Button
                                size="md"
                                colorScheme="charcoal"
                                variant="outline"
                                borderColor="muted.50"
                                fontWeight="medium"
                                loadingText="Preparing..."
                                isLoading={downloadEmailMutation.isPending}
                                isDisabled={mutation?.isPending || isUploadingImage}
                                onClick={onDownloadClick}
                            >
                                <FaIcon icon={faArrowDownToLine} color="muted.300" mr={2} />
                                Download
                            </Button>
                        )}

                    {previewAvailable && (
                        <Button
                            size="md"
                            colorScheme="charcoal"
                            variant="outline"
                            borderColor="muted.50"
                            fontWeight="medium"
                            isDisabled={mutation?.isPending || isUploadingImage}
                            onClick={async () => {
                                await trigger();
                                previewDisclosure.onOpen();
                            }}
                        >
                            <FaIcon icon={faMagnifyingGlass} color="muted.300" mr={2} />
                            Preview
                        </Button>
                    )}

                    <Button
                        colorScheme="secondary"
                        size="md"
                        variant="solid"
                        isDisabled={!emailSet || isUploadingImage}
                        onClick={handleSubmit(onSubmit)}
                        isLoading={mutation?.isPending}
                        loadingText="Send"
                    >
                        <FaIcon icon={faPaperPlane} mr={2} />
                        <Text>{confirmButtonText ?? 'Send'}</Text>
                    </Button>
                </HStack>

                <SendEmailPreviewModal
                    isValid={isValid && emailSet}
                    formErrors={formErrors}
                    onSubmit={handleSubmit(onSubmit)}
                    isOpen={previewDisclosure.isOpen}
                    onClose={previewDisclosure.onClose}
                    metaData={metaData}
                    isLoading={mutation?.isPending}
                    formValues={getValues()}
                />
            </VStack>
        );
    }
);
